import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { subDays } from 'date-fns';
import { useEffectOnce } from 'react-use';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import stackBillsIcon from '@/enterprise/assets/stackbills.svg?url';
import { OfferBuilderNavigationState } from '@/enterprise/pages/OfferBuilder';
import useFeature from '@/lib/features';
import { useReporting } from '@/reporting';
import { ButtonLink } from '@/shared/components/ButtonLink';
import useExchangeRates from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import { isProd } from '@/utils/env';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useLocaleFormat from '@/utils/useLocaleFormat';
import usePricingSuggestion from '../data/usePricingSuggestion';
import { TakerMarket } from '../data/useTakerMarkets';
import { OfferBuilderLocation } from './OfferBuilder/OfferBuilderContext';
import { TakerMarketWithRate, RateMap } from './OfferBuilder/rateHelpers';
import {
  buildTakerMarketKey,
  calculateBlendedRate,
  updateWorkingTakerMarketsWithRates,
  ensureWorkingTakerMarkets,
  isExpressAcceptEligible,
  calculateDiscount,
} from './OfferBuilder/utils';

interface TakerMarketWithRateInfo extends TakerMarketWithRate {
  pricingRateUuid?: string;
}

export const useEnableNewDivisionAlert = () => {
  const [enableNewDivisionAlert] = useFeature('enterprise-ui_enableNewDivisionAlert');

  return enableNewDivisionAlert;
};

// Only checks eligibility from taker market data, does not call pricing suggestion
// If there are multiple buyers across the divisions, this will return the taker markets for the buyer
// with the highest eligible amount. This won't actually happen on initial release
// as we're only releasing for BJs, but future proofing just in case
export const getNewDivisionAlertEligibleTakerMarkets = (takerMarkets: TakerMarket[]) => {
  const isProdEnv = isProd();
  const eligibleTms = takerMarkets.filter(
    (tm) =>
      tm.userAddedToDivision &&
      tm.userAddedToDivision >= subDays(new Date(), 90).toISOString() &&
      isExpressAcceptEligible([tm]) &&
      !tm.lastClearDate &&
      // only show alert for BJs in prod
      (isProdEnv ? tm.makerOrganizationUuid === '1afd57ed-72fa-4b33-9987-14a013927086' : true)
  );

  if (eligibleTms.length === 0) {
    return [];
  }

  // Group by buyer and track the one with highest eligible amount
  let highestBuyer: { totalEligibleAmountUsd: number; takerMarkets: TakerMarket[] } | null = null;
  const buyerTotals: Record<string, { totalEligibleAmountUsd: number; takerMarkets: TakerMarket[] }> = {};

  for (const tm of eligibleTms) {
    if (!buyerTotals[tm.makerOrganizationUuid]) {
      buyerTotals[tm.makerOrganizationUuid] = {
        totalEligibleAmountUsd: 0,
        takerMarkets: [],
      };
    }

    const buyer = buyerTotals[tm.makerOrganizationUuid];
    buyer.totalEligibleAmountUsd += tm.eligibleInvoiceAmountUsd;
    buyer.takerMarkets.push(tm);

    // Track the buyer with the highest amount
    if (!highestBuyer || buyer.totalEligibleAmountUsd > highestBuyer.totalEligibleAmountUsd) {
      highestBuyer = buyer;
    }
  }

  return highestBuyer?.takerMarkets ?? [];
};

// Checks eligibility from taker market data and pricing suggestion data
// returns taker markets with rates from pricing suggestion
export const useGetNewDivisionAlertData = (takerMarkets: TakerMarket[]) => {
  const enableNewDivisionAlert = useEnableNewDivisionAlert();

  // isExpressAcceptEligible checks if taker configs are disabled
  const newEAEligibleTakerMarkets = useMemo(
    () =>
      // Prevent calling usePricingSuggestion if feature flag is disabled
      enableNewDivisionAlert ? getNewDivisionAlertEligibleTakerMarkets(takerMarkets) : [],
    [takerMarkets, enableNewDivisionAlert]
  );

  // Handles requirement for pricing enabled, data will be null if pricing is disabled
  const { data: pricingData } = usePricingSuggestion({ takerMarkets: newEAEligibleTakerMarkets });

  const optimalRateType = (pricingData?.overallOptimalRateType ?? 'APR') as 'APR' | 'DISC';

  const takerMarketsWithRate: TakerMarketWithRateInfo[] = useMemo(() => {
    // If no pricing data, return empty array
    if (!pricingData || !pricingData.supplierMarkets) {
      return [];
    }

    const rateTypeKey = optimalRateType === 'DISC' ? 'discount' : 'apr';
    // Filter out any pricing suggestions that don't have express accept pricing
    const pricingDataWithEA = pricingData.supplierMarkets.filter((sm) => !!sm[rateTypeKey].expressAccept.trendingRate);

    return newEAEligibleTakerMarkets
      .map((tm) => {
        const matchPricing = pricingDataWithEA.find(
          (pricingTm) =>
            pricingTm.marketUuid === tm.marketUuid && pricingTm.supplierDivisionUuid === tm.takerDivisionUuid
        );

        if (matchPricing) {
          return {
            takerMarket: tm,
            rate: matchPricing[rateTypeKey].expressAccept.trendingRate,
            rateType: optimalRateType,
            pricingStrategy: 'expressAccept',
            pricingRateUuid: matchPricing[rateTypeKey].expressAccept.pricingRateUuid,
          } satisfies TakerMarketWithRateInfo;
        }
      })
      .filter((tmWithRate) => tmWithRate !== undefined);
  }, [pricingData, newEAEligibleTakerMarkets, optimalRateType]);

  return {
    takerMarketsWithRate,
    optimalRateType,
  };
};

// Builds navigation state for new division alert using taker markets with rates from pricing suggestion
export const buildNewDivisionAlertNavigationState = (
  takerMarketsWithRate: TakerMarketWithRateInfo[],
  optimalRateType: 'APR' | 'DISC',
  location?: OfferBuilderLocation,
  source?: string
): OfferBuilderNavigationState => {
  // create rate map for all taker markets with pricing data
  const rateMap: RateMap = takerMarketsWithRate.reduce((map, { takerMarket, rate, pricingRateUuid }) => {
    const key = buildTakerMarketKey(takerMarket);
    map[key] = {
      rate: rate!,
      rateType: optimalRateType,
      suggestedRate: rate!,
      suggestRateType: optimalRateType,
      pricingStrategy: 'expressAccept',
      pricingRateUuid,
    };
    return map;
  }, {} as RateMap);

  // create working taker markets
  const selectedMarkets = takerMarketsWithRate.map((tm) => tm.takerMarket);
  const initialWorkingTakerMarkets = ensureWorkingTakerMarkets({}, selectedMarkets);
  const workingTakerMarkets = updateWorkingTakerMarketsWithRates(initialWorkingTakerMarkets, rateMap);

  return {
    disableNavigation: true,
    frequency: 'oneTime',
    initialStep: 'review',
    preselectedTakerMarketIds: takerMarketsWithRate.map((tm) => tm.takerMarket.id),
    pricingStrategy: 'expressAccept',
    rateType: optimalRateType,
    reviewOnly: true,
    location,
    source,
    workingTakerMarkets,
  } satisfies OfferBuilderNavigationState;
};

/**
 * Requirements for this alert to show:
 * User is not managed by ESRM account
 * makerOrg is BJs (MVP)
 * User added to BJs div(s) in the last 90 days
 * User is pricing enabled
 * TMs are EA eligible from pricing
 * Taker configs are disabled
 * Taker market has not cleared before
 */
const NewDivisionAlert = ({
  takerMarkets,
  referrer,
  className,
}: {
  takerMarkets: TakerMarket[];
  referrer: OfferBuilderLocation;
  className?: string;
}) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { takerMarketsWithRate, optimalRateType } = useGetNewDivisionAlertData(takerMarkets);
  const { asPercent, asCurrency } = useLocaleFormat();
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();

  const showAlert = takerMarketsWithRate.length > 0;

  useEffectOnce(() => {
    if (showAlert) {
      trackEnterpriseEvent('new-division-alert::shown', {
        referrer,
        takerMarkets: takerMarketsWithRate.map((tm) => ({
          takerDivisionUuid: tm.takerMarket.takerDivisionUuid,
          marketUuid: tm.takerMarket.marketUuid,
          makerOrganizationUuid: tm.takerMarket.makerOrganizationUuid,
        })),
      });
    }
  });

  if (!showAlert) {
    return null;
  }

  const navigationState = buildNewDivisionAlertNavigationState(
    takerMarketsWithRate,
    optimalRateType,
    referrer,
    'new-division-alert'
  );

  const blendedRate = calculateBlendedRate({
    items: takerMarketsWithRate.map((tmWithRate) => ({
      dpeWeightedAvg: tmWithRate.takerMarket.eligibleDpeWeightedAvg,
      dpeWeightedSum: tmWithRate.takerMarket.eligibleDpeWeightedSum,
      invoiceAmount: tmWithRate.takerMarket.eligibleInvoiceAmount,
      isDiscount: tmWithRate.rateType === 'DISC',
      // pricingDataWithEA is filtered to only include tms with ea pricing
      rate: tmWithRate.rate!,
    })),
    rateType: optimalRateType,
  });

  const estimatedEarlyPaymentAmount = takerMarketsWithRate.reduce((acc, takerMarketWithRate) => {
    const { rate, rateType, takerMarket } = takerMarketWithRate;

    const convertedEligibleInvoiceAmount = convertCurrency({
      amount: takerMarket.eligibleInvoiceAmount,
      exchangeRates,
      from: takerMarket.currency,
      to: selectedCurrency,
    });
    const discount = convertCurrency({
      amount: calculateDiscount({
        dpeWeightedAvg: takerMarket.eligibleDpeWeightedAvg,
        invoiceAmount: takerMarket.eligibleInvoiceAmount,
        isDiscount: rateType === 'DISC',
        rate: rate ?? 0,
      }),
      exchangeRates,
      from: takerMarket.currency,
      to: selectedCurrency,
    });

    acc += convertedEligibleInvoiceAmount - discount;
    return acc;
  }, 0);

  const amountString = asCurrency(estimatedEarlyPaymentAmount, selectedCurrency);
  const rateString = `${asPercent({ decimals: 2, value: blendedRate })} ${
    optimalRateType === 'DISC' ? t('core.discount') : t('core.aprAbbreviation')
  }`;
  // First buyer name ok since getNewDivisionAlertEligibleTakerMarkets ensures only one buyer
  const buyerName = takerMarketsWithRate[0].takerMarket.makerOrganizationName;
  const useBlendedDescription = !!takerMarketsWithRate.find(
    (tmWithRate) =>
      // pricingDataWithEA is filtered to only include tms with ea pricing
      // compare rates to 2 decimal places
      // tmWithRate.rate comes from pricing suggestion which returns rate as percentage e.g. 0.65 for 0.65%
      // blendedRate calculates rate in decimal e.g. 0.0065 for 0.65%, hence the extra * 100
      Math.round(tmWithRate.rate! * 100) / 100 !== Math.round(blendedRate * 100 * 100) / 100
  );

  return (
    <div className={className}>
      <div className="flex flex-col items-center justify-between gap-2 rounded-md bg-info-200 px-4 py-3 text-info-900 md:flex-row">
        <div className="flex items-center gap-4">
          <img src={stackBillsIcon} alt="stack of bills" />
          <div>
            <p className="font-bold">{t('newDivisionOffer.title')}</p>
            <p>
              {t(useBlendedDescription ? 'newDivisionOffer.description.blended' : 'newDivisionOffer.description', {
                amount: amountString,
                rate: rateString,
                buyerName,
              })}
            </p>
          </div>
        </div>
        <ButtonLink
          className="w-full md:w-fit"
          to={asEnterpriseLink('build-offer/review')}
          state={navigationState}
          onClick={() =>
            trackEnterpriseEvent('new-division-alert::cta::clicked', {
              referrer,
              takerMarkets: takerMarketsWithRate.map((tm) => ({
                takerDivisionUuid: tm.takerMarket.takerDivisionUuid,
                marketUuid: tm.takerMarket.marketUuid,
                makerOrganizationUuid: tm.takerMarket.makerOrganizationUuid,
              })),
              rate: rateString,
            })
          }
          size="sm"
        >
          {t('core.reviewOffer')}
        </ButtonLink>
      </div>
    </div>
  );
};

export default NewDivisionAlert;
